/**
 * ICENFIRE — Global Duality State Manager
 * Manages 'ice' and 'fire' aesthetics across the entire Shopify storefront.
 */
class IcenfireThemeManager {
  constructor() {
    this.storageKey = 'icenfire_theme';
    this.cookieName = 'icenfire_theme';
    this.validThemes = ['ice', 'fire'];
    this.body = document.body;
    this.root = document.documentElement;

    this.init();
  }

  init() {
    const savedTheme = this.getSavedTheme();

    if (savedTheme) {
      this.applyTheme(savedTheme, { animateTransition: false });
      this.dismissGatewayImmediate();
    } else {
      // Default underlying store state before user selects on gateway
      this.applyTheme('ice', { animateTransition: false, persist: false });
      this.openGateway();
    }

    this.bindGatewayEvents();
    this.bindToggleEvents();
  }

  getSavedTheme() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (this.validThemes.includes(stored)) return stored;
    } catch (err) {
      // Fallback to cookie if localStorage is restricted
      const match = document.cookie.match(new RegExp('(^| )' + this.cookieName + '=([^;]+)'));
      if (match && this.validThemes.includes(match[2])) return match[2];
    }
    return null;
  }

  saveTheme(theme) {
    try {
      localStorage.setItem(this.storageKey, theme);
    } catch (err) {
      // Ignore storage quota errors
    }
    // Also set a 30-day cookie so Shopify cart attributes or edge workers can read it if needed
    document.cookie = `${this.cookieName}=${theme}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
  }

  /**
   * Applies `.theme-ice` or `.theme-fire` to both <body> and <html>
   */
  applyTheme(theme, options = { animateTransition: true, persist: true }) {
    if (!this.validThemes.includes(theme)) return;

    if (options.animateTransition) {
      this.body.classList.add('theme-transitioning');
      window.setTimeout(() => {
        this.body.classList.remove('theme-transitioning');
      }, 650);
    }

    this.validThemes.forEach((t) => {
      this.body.classList.remove(`theme-${t}`);
      this.root.classList.remove(`theme-${t}`);
    });

    this.body.classList.add(`theme-${theme}`);
    this.root.classList.add(`theme-${theme}`);
    this.root.setAttribute('data-theme', theme);

    if (options.persist !== false) {
      this.saveTheme(theme);
    }

    this.updateToggles(theme);

    window.dispatchEvent(
      new CustomEvent('icenfire:themechange', {
        detail: { theme }
      })
    );
  }

  toggleTheme() {
    const current = this.root.getAttribute('data-theme') === 'fire' ? 'fire' : 'ice';
    const next = current === 'ice' ? 'fire' : 'ice';
    this.applyTheme(next, { animateTransition: true, persist: true });
  }

  bindGatewayEvents() {
    const gateway = document.querySelector('[data-icenfire-gateway]');
    if (!gateway) return;

    const panels = gateway.querySelectorAll('[data-gateway-choice]');

    panels.forEach((panel) => {
      const choice = panel.getAttribute('data-gateway-choice');

      // Hover state classes on parent for fluid 50/50 -> 62/38 split expansion
      panel.addEventListener('mouseenter', () => {
        gateway.setAttribute('data-hover-state', choice);
      });

      panel.addEventListener('mouseleave', () => {
        gateway.removeAttribute('data-hover-state');
      });

      // Click handler: expand chosen side to 100vw, set global theme, then reveal storefront
      panel.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectFromGateway(choice, gateway);
      });
    });

    // Optional trigger to re-open the gateway from anywhere (e.g. footer link)
    document.querySelectorAll('[data-open-gateway]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openGateway();
      });
    });
  }

  selectFromGateway(choice, gateway) {
    if (!this.validThemes.includes(choice)) return;

    // 1. Apply the theme immediately so the underlying storefront is styled
    this.applyTheme(choice, { animateTransition: false, persist: true });

    // 2. Trigger the full-bleed portal expansion animation on the gateway
    gateway.setAttribute('data-selected-state', choice);
    gateway.classList.add('is-exiting');

    window.setTimeout(() => {
      this.root.classList.remove('gateway-pending');
      this.root.classList.add('gateway-resolved');
      this.body.classList.remove('gateway-pending');
      gateway.setAttribute('aria-hidden', 'true');
      gateway.classList.remove('is-exiting');
      gateway.removeAttribute('data-selected-state');
    }, 850);
  }

  dismissGatewayImmediate() {
    const gateway = document.querySelector('[data-icenfire-gateway]');
    this.root.classList.remove('gateway-pending');
    this.root.classList.add('gateway-resolved');
    this.body.classList.remove('gateway-pending');
    if (gateway) {
      gateway.setAttribute('aria-hidden', 'true');
    }
  }

  openGateway() {
    const gateway = document.querySelector('[data-icenfire-gateway]');
    if (!gateway) return;
    this.root.classList.remove('gateway-resolved');
    this.root.classList.add('gateway-pending');
    this.body.classList.add('gateway-pending');
    gateway.setAttribute('aria-hidden', 'false');
  }

  bindToggleEvents() {
    document.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetTheme = btn.getAttribute('data-theme-target');
        if (targetTheme) {
          this.applyTheme(targetTheme, { animateTransition: true, persist: true });
        } else {
          this.toggleTheme();
        }
      });
    });
  }

  updateToggles(activeTheme) {
    document.querySelectorAll('[data-theme-switcher]').forEach((switcher) => {
      switcher.setAttribute('data-active-theme', activeTheme);
      switcher.querySelectorAll('[data-theme-target]').forEach((btn) => {
        const isCurrent = btn.getAttribute('data-theme-target') === activeTheme;
        btn.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
        btn.classList.toggle('is-active', isCurrent);
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.IcenfireTheme = new IcenfireThemeManager();
});